"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { toggleAccordionCategory } from "./download-product-navigation-utils";
import type { DownloadProductCategory } from "./download-types";

type DownloadProductNavigationTreeState = {
  expandedCategoryIds: ReadonlySet<number>;
  expandedForProductId: number | null;
};

type DownloadProductNavigationState = {
  ensureProductCategoryPath: (
    productId: number,
    categoryIds: readonly number[],
  ) => void;
  expandedCategoryIds: ReadonlySet<number>;
  expandedForProductId: number | null;
  query: string;
  readScrollTop: () => number;
  rememberScrollTop: (scrollTop: number) => void;
  setQuery: (query: string) => void;
  toggleCategory: (
    categoryId: number,
    categories: readonly DownloadProductCategory[],
  ) => void;
};

const DownloadProductNavigationStateContext =
  createContext<DownloadProductNavigationState | null>(null);

/** 管理目录树的产品路径初始化与用户折叠状态。 */
function useDownloadProductNavigationTreeState() {
  const [treeState, setTreeState] = useState<DownloadProductNavigationTreeState>(
    {
      expandedCategoryIds: new Set(),
      expandedForProductId: null,
    },
  );
  const ensureProductCategoryPath = useCallback(
    (productId: number, categoryIds: readonly number[]) => {
      setTreeState((previous) => {
        if (previous.expandedForProductId === productId) return previous;
        return {
          expandedCategoryIds: new Set(categoryIds),
          expandedForProductId: productId,
        };
      });
    },
    [],
  );
  const toggleCategory = useCallback(
    (categoryId: number, categories: readonly DownloadProductCategory[]) => {
      setTreeState((previous) => ({
        ...previous,
        expandedCategoryIds: toggleAccordionCategory(
          previous.expandedCategoryIds,
          categoryId,
          categories,
        ),
      }));
    },
    [],
  );
  return { ensureProductCategoryPath, toggleCategory, treeState };
}

/** 在下载路由内持久保存目录搜索、展开和滚动状态。 */
export function DownloadProductNavigationStateProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [query, setQuery] = useState("");
  const { ensureProductCategoryPath, toggleCategory, treeState } =
    useDownloadProductNavigationTreeState();
  const scrollTopRef = useRef(0);
  const readScrollTop = useCallback(() => scrollTopRef.current, []);
  const rememberScrollTop = useCallback((scrollTop: number) => {
    scrollTopRef.current = scrollTop;
  }, []);
  const value = useMemo(
    () => ({
      ensureProductCategoryPath,
      expandedCategoryIds: treeState.expandedCategoryIds,
      expandedForProductId: treeState.expandedForProductId,
      query,
      readScrollTop,
      rememberScrollTop,
      setQuery,
      toggleCategory,
    }),
    [
      ensureProductCategoryPath,
      query,
      readScrollTop,
      rememberScrollTop,
      treeState,
      toggleCategory,
    ],
  );

  return (
    <DownloadProductNavigationStateContext.Provider value={value}>
      {children}
    </DownloadProductNavigationStateContext.Provider>
  );
}

/** 读取下载路由持久化的目录交互状态。 */
export function useDownloadProductNavigationState() {
  const state = useContext(DownloadProductNavigationStateContext);
  if (!state) {
    throw new Error("下载产品导航必须位于状态 Provider 内。");
  }
  return state;
}
